import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Paper, Typography, Grid, TextField, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Chip, Tooltip, Button, useTheme,
} from "@mui/material";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useDispatch } from "react-redux";
import httpClient from "../../_util/api";
import { showLoader, hideLoader } from "../../Store/mainSlice";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";

const DAY_MS = 24 * 60 * 60 * 1000;

const PRESETS = {
  "7d": { label: "Last 7 days", days: 7 },
  week: { label: "This week" },
  "30d": { label: "Last 30 days", days: 30 },
  custom: { label: "Custom" },
};

const toInputDate = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const rangeForPreset = (preset) => {
  const today = startOfDay(new Date());
  if (preset === "week") {
    // Week starts on Monday
    const offset = (today.getDay() + 6) % 7;
    return { from: toInputDate(new Date(today.getTime() - offset * DAY_MS)), to: toInputDate(today) };
  }
  const days = PRESETS[preset]?.days || 7;
  return { from: toInputDate(new Date(today.getTime() - (days - 1) * DAY_MS)), to: toInputDate(today) };
};

const parseInputDate = (value, endOfDay = false) => {
  const [y, m, d] = value.split("-").map(Number);
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;
const fmtDay = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
const fmtMoney = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
const fmtDays = (n) => (n === null || n === undefined ? "—" : `${n} day${n === 1 ? "" : "s"}`);

const PARTNER_OVERDUE_DAYS = 7;

const pendingDays = (a) => (a.approvedAt && !a.paidAt ? Math.floor((Date.now() - new Date(a.approvedAt)) / DAY_MS) : null);

// Paid: how long the partner took. Not paid: how long it has been waiting with the partner.
const partnerFlag = (a) => {
  if (!a.vendorName || !a.approvedAt) return null;
  if (a.paidAt) {
    return <Chip size="small" color="success" label={`Paid in ${fmtDays(a.daysToPay)}`} />;
  }
  const waiting = pendingDays(a);
  return (
    <Chip
      size="small"
      color={waiting > PARTNER_OVERDUE_DAYS ? "error" : "warning"}
      label={`Pending ${fmtDays(waiting)}`}
    />
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <Paper sx={{ p: 2, height: "100%", borderTop: `4px solid ${color}` }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// One cell per stage: date, who did it, and how long it took since the previous stage.
const StageCell = ({ date, by, gap, color, pendingLabel, flag }) => (
  <TableCell sx={{ minWidth: 140 }}>
    {date ? (
      <Box sx={{ borderLeft: `3px solid ${color}`, pl: 1 }}>
        <Typography variant="body2" fontWeight={600}>{fmtDate(date)}</Typography>
        {by && <Typography variant="caption" color="text.secondary" display="block">{by}</Typography>}
        {gap !== undefined && gap !== null && (
          <Typography variant="caption" sx={{ color }}>+{fmtDays(gap)}</Typography>
        )}
        {flag && <Box sx={{ mt: 0.5 }}>{flag}</Box>}
      </Box>
    ) : (
      <Chip size="small" variant="outlined" label={pendingLabel || "—"} />
    )}
  </TableCell>
);

const ApprovalTracker = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const snackbar_Ref = useRef();
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(rangeForPreset("7d"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [appNumber, setAppNumber] = useState("");

  // Wait until the user stops typing before searching.
  useEffect(() => {
    const t = setTimeout(() => setAppNumber(search.trim()), 500);
    return () => clearTimeout(t);
  }, [search]);

  const colors = {
    reviewed: theme.palette.info.main,
    approved: theme.palette.success.main,
    partner: theme.palette.primary.main,
    paid: theme.palette.secondary.main,
    waiting: theme.palette.warning.main,
  };

  const load = useCallback(async () => {
    if (!range.from || !range.to) return;
    setLoading(true);
    dispatch(showLoader());
    try {
      const params = new URLSearchParams({
        from: parseInputDate(range.from).toISOString(),
        to: parseInputDate(range.to, true).toISOString(),
      });
      if (appNumber) params.set("applicationNumber", appNumber);
      const res = await httpClient.get(`/report/approval-tracker?${params.toString()}`);
      setData(res.data);
      setPage(0);
    } catch (error) {
      snackbar_Ref.current?.showMessage(
        "error",
        error?.response?.data?.message || "Failed to load approval tracker",
        "",
        "i-chk-circle"
      );
    } finally {
      setLoading(false);
      dispatch(hideLoader());
    }
  }, [range, appNumber, dispatch]);

  useEffect(() => { load(); }, [load]);

  const onPresetChange = (_, value) => {
    if (!value) return;
    setPreset(value);
    if (value !== "custom") setRange(rangeForPreset(value));
  };

  const isDefaultFilter = preset === "7d" && statusFilter === "all" && !search &&
    range.from === rangeForPreset("7d").from && range.to === rangeForPreset("7d").to;

  const clearFilters = () => {
    setPreset("7d");
    setRange(rangeForPreset("7d"));
    setStatusFilter("all");
    setSearch("");
    setAppNumber("");
    setPage(0);
  };

  // Per-day counts inside the selected range.
  const dailyData = useMemo(() => {
    if (!data) return [];
    const from = parseInputDate(range.from);
    const to = parseInputDate(range.to);
    const days = [];
    for (let d = from; d <= to; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      days.push({ key: toInputDate(d), day: fmtDay(d), Approved: 0, Paid: 0 });
    }
    const byKey = new Map(days.map((d) => [d.key, d]));
    data.applications.forEach((a) => {
      if (a.approvedAt) {
        const row = byKey.get(toInputDate(new Date(a.approvedAt)));
        if (row) row.Approved += 1;
      }
      if (a.paidAt) {
        const row = byKey.get(toInputDate(new Date(a.paidAt)));
        if (row) row.Paid += 1;
      }
    });
    return days;
  }, [data, range]);

  const rows = useMemo(() => {
    const all = data?.applications || [];
    if (statusFilter === "paid") return all.filter((a) => a.status === "paid");
    if (statusFilter === "waiting") return all.filter((a) => a.status !== "paid");
    return all;
  }, [data, statusFilter]);

  const s = data?.summary;

  const pendingList = (data?.applications || []).map(pendingDays).filter((d) => d !== null);
  const pendingSummary = pendingList.length
    ? `Avg ${fmtDays(Math.round(pendingList.reduce((t, d) => t + d, 0) / pendingList.length))} · Oldest ${fmtDays(Math.max(...pendingList))}`
    : null;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <CustomizedSnackbars ref={snackbar_Ref} />

      <Typography variant="h5" fontWeight={700}>Approved Applications Tracker</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Applications approved by the final approver in the selected period, and how each moved from
        reviewer to approver to payment.
      </Typography>

      {/* Date range */}
      <Paper sx={{ p: 2, mb: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        <TextField
          size="small" label="Application #" placeholder="e.g. 7234"
          value={search}
          onChange={(e) => setSearch(e.target.value.replace(/\D/g, ""))}
          inputProps={{ inputMode: "numeric" }}
          helperText={appNumber ? "Searching all dates" : ""}
          sx={{ width: 170 }}
        />
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={onPresetChange} disabled={!!appNumber}>
          {Object.entries(PRESETS).map(([key, p]) => (
            <ToggleButton key={key} value={key}>{p.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
          value={range.from} inputProps={{ max: range.to }} disabled={!!appNumber}
          onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }}
        />
        <TextField
          size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
          value={range.to} inputProps={{ min: range.from, max: toInputDate(new Date()) }} disabled={!!appNumber}
          onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }}
        />
        <Button size="small" variant="outlined" onClick={clearFilters} disabled={isDefaultFilter}>
          Clear filters
        </Button>
      </Paper>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Approved" value={s?.approved ?? "—"} sub={s && fmtMoney(s.approvedAmount)} color={colors.approved} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Paid" value={s?.paid ?? "—"} sub={s && fmtMoney(s.paidAmount)} color={colors.paid} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Awaiting payment" value={s?.awaitingPayment ?? "—"} sub={pendingSummary} color={colors.waiting} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Avg. time per stage"
            value={s ? fmtDays(s.avgDaysToApprove) : "—"}
            sub={s && `Submit→Review ${fmtDays(s.avgDaysToReview)} · Approve→Paid ${fmtDays(s.avgDaysToPay)}`}
            color={colors.reviewed}
          />
        </Grid>
      </Grid>

      {/* Day by day */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Day by day</Typography>
        <Box sx={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={dailyData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <ChartTooltip />
              <Legend />
              <Bar dataKey="Approved" fill={colors.approved} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Paid" fill={colors.paid} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Per-application journey */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Application journey</Typography>
          <ToggleButtonGroup
            size="small" exclusive value={statusFilter}
            onChange={(_, v) => { if (v) { setStatusFilter(v); setPage(0); } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="waiting">Awaiting payment</ToggleButton>
            <ToggleButton value="paid">Paid</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>App #</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Reviewer</TableCell>
                <TableCell>Approver</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((a) => (
                <TableRow key={a.applicationId} hover>
                  <TableCell>{a.applicationNumber}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="body2" fontWeight={600}>{a.studentName || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{a.institution}</Typography>
                    <Typography variant="caption" color="text.secondary">{fmtMoney(a.approvedFee)}</Typography>
                  </TableCell>
                  <StageCell date={a.submittedAt} color={theme.palette.grey[500]} />
                  <StageCell date={a.reviewedAt} by={a.reviewerName} gap={a.daysToReview} color={colors.reviewed} />
                  <StageCell date={a.approvedAt} by={a.approverName} gap={a.daysToApprove} color={colors.approved} />
                  <StageCell date={a.vendorName ? a.approvedAt : null} by={a.vendorName} color={colors.partner} pendingLabel="Not assigned" flag={partnerFlag(a)} />
                  <StageCell date={a.paidAt} by={a.paidBy} gap={a.daysToPay} color={colors.paid} pendingLabel="Awaiting payment" />
                  <TableCell align="right">
                    <Tooltip title={a.paidAt ? "Submitted → Paid" : "Submitted → Approved"}>
                      <Typography variant="body2" fontWeight={600}>{fmtDays(a.totalDays)}</Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {appNumber
                      ? `Application #${appNumber} not found, or it has not been approved yet.`
                      : "No approved applications in this period."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
};

export default ApprovalTracker;
